import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  Section,
  Heading,
  Paragraph,
  HorizontalRule,
  FormLayout,
  Reveal,
  Callout,
  UnorderedList,
  ListItem,
} from '@piggybank/core';
import i18n from 'i18next';
import { useHistory } from 'react-router-dom';
import {
  Form,
  FormFeedback,
  Field,
  Label,
  Select,
  TextInput,
  FieldFeedback,
  YesNoRadio,
  Fieldset,
  Hint,
  Radio,
} from '@piggybank/form';
import { useTranslation } from 'react-i18next';
import { getUserSessionDataByKey, setUserSessionDataByKey } from 'storage/userStorage';
import { Country, ReasonForOpening as ReasonForOpeningList, CartAddress as CartAddressList } from 'referenceData';
import { WithI18nProgressIndicator } from 'components/WithI18nPiggybank';
import { ButtonWithBack } from 'components/ButtonWithBack';
import { linkTagging, useTagging, validationTagging } from 'tealiumTrack';
import { rePrevious } from 'utils/util';
import { CHINA_CODE, HKSAR, CHINA, NO } from '../../../referenceData/constants';
import addressModal from 'storage/page/address';
import { getCNAddresses } from 'utils/getCNAddresses';
import './custom.css';

const TAG_PAGE_URL = '/forms/your-address';
const TAG_PAGE_NAME = 'pws:forms:your address';

export const hasValidationErrors = (errors) => Object.keys(errors || {}).length > 0;

const LINE_ERROR_MESSAGE = 'ICO.ADDRESS.LINE_ERROR_MESSAGE';
const LINE_ERROR_MESSAGE_LT = 'ICO.ADDRESS.LINE_ERROR_MESSAGE_LT';
const SPECIAL_ERROR = 'ICO.COMMON.SPECIAL_ERROR';
const LINE_ERROR_MESSAGE1 = 'ICO.ADDRESS.LINE_ERROR_MESSAGE1';
const LINE_ERROR_MESSAGE_LT1 = 'ICO.ADDRESS.LINE_ERROR_MESSAGE_LT1';
const LINE_ERROR_MESSAGE_LT2 = 'ICO.ADDRESS.LINE_ERROR_MESSAGE_LT2';
const LINE_ERROR_MESSAGE2 = 'ICO.ADDRESS.LINE_ERROR_MESSAGE2';
const POSTALCODEERROR = 'ICO.ADDRESS.POSTALCODEERROR';
const POSTALCODE_ERROE = 'ICO.ADDRESS.POSTALCODE_ERROE';
const CURRENT_ERRORS = 'ICO.ADDRESS.CURRENT_ERRORS';
const COUNTRY_ERROR_MESSAGE = 'ICO.ADDRESS.COUNTRY_ERROR_MESSAGE';

const checkAddressCountry = (path, values) => {
  return path === 'currentResidentialAddress' 
    ? values.currentResidentialAddress?.addressCountry === CHINA 
    : values.correspondenceAddress?.addressCountry === CHINA;
}

const getOptions = (obj = {}) => {
  const options = [];
  for (const key in obj) {
    options.push({
      value: key,
      label: obj[key]
    });
  }
  return options;
};

const defaultAddressValues = addressModal.address.currentResidentialAddress;

const addressSchema = yup.object().shape({
  addressCountry: yup
    .string()
    .required(() => i18n.t(COUNTRY_ERROR_MESSAGE)),
  provinceCode: yup.string().when('addressCountry', {
    is: (val) => val === CHINA,
    then: yup.string().required(() => i18n.t('ICO.ADDRESS.PROVINCE_ERROR_MESSAGE')),
  }),
  cityCode: yup.string().when('addressCountry', {
    is: (val) => val === CHINA,
    then: yup.string().required(() => i18n.t('ICO.ADDRESS.CITY_ERROR_MESSAGE')),
  }),
  districtCode: yup.string().when('addressCountry', {
    is: (val) => val === CHINA,
    then: yup.string().required(() => i18n.t('ICO.ADDRESS.DISTRICT_ERROR_MESSAGE')),
  }),
  addressLine3: yup.string().when('addressCountry', {
    is: (val) => val !== CHINA,
    then: yup
      .string()
      .required(() => i18n.t(LINE_ERROR_MESSAGE2))
      .max(24, ({ max }) => i18n.t(LINE_ERROR_MESSAGE_LT2, { count: max }))
      .matches(/^[\u4300-\u9fa5A-Za-z0-9.\-/, ]*$/, () => i18n.t(SPECIAL_ERROR)),
  }),
  addressLine2: yup
    .string()
    .required(() => i18n.t(LINE_ERROR_MESSAGE1))
    .max(35, ({ max }) => i18n.t(LINE_ERROR_MESSAGE_LT1, { count: max }))
    .matches(/^[\u4300-\u9fa5A-Za-z0-9.\-/, ]*$/, () => i18n.t(SPECIAL_ERROR)),
  addressLine1: yup
    .string()
    .required(() => i18n.t(LINE_ERROR_MESSAGE))
    .max(35, ({ max }) => i18n.t(LINE_ERROR_MESSAGE_LT, { count: max }))
    .matches(/^[\u4300-\u9fa5A-Za-z0-9.\-/, ]*$/, () => i18n.t(SPECIAL_ERROR)),
  postalCode: yup
    .string()
    .required(() => i18n.t(POSTALCODEERROR))
    .max(10, ({ max }) => i18n.t(POSTALCODE_ERROE, { count: max }))
    .matches(/^[A-Za-z 0-9_-]*$/, () => i18n.t(CURRENT_ERRORS)),
});

const schema = yup.object().shape({
  currentResidentialAddress: addressSchema,
  correspondenceAddressDifferent: yup
    .string()
    .matches(/(yes|no)/)
    .required(() => i18n.t('ICO.ADDRESS.CORRESPONDENCE_SUB_TITLE')),
  correspondenceAddress: yup.object().when('correspondenceAddressDifferent', {
    is: val => val === NO,
    then: addressSchema
  }),
  cartAddress: yup.string().required(() => i18n.t('ICO.ADDRESS.CARDADDRESS_ERROR_MESSAGE')),
  atmCountry: yup.string().when('cartAddress', {
    is: val => val === '2',
    then: yup.string()
      .required(() => i18n.t('ICO.ADDRESS.ATMCARD_ERROR_MESSAGE'))
      .max(35, ({ max }) => i18n.t('ICO.ADDRESS.ATMCARD_ERROR_MESSAGE_LT', { count: max }))
      .matches(/^[\u4300-\u9fa5A-Za-z0-9.\-/, ]*$/, () => i18n.t(SPECIAL_ERROR))
  }),
  selectReasonsForOpeningAccount: yup.string().when('currentResidentialAddress', {
    is: (val) => val.addressCountry !== '' && val.addressCountry !== HKSAR,
    then: yup.string()
      .required(() => i18n.t('ICO.ADDRESS.APPLICATION_SUB_TITLE_ERROR_MESSAGE')),
  }),
  otherReasonForOpeningAccountInAnotherCountryText: yup.string().when('selectReasonsForOpeningAccount', {
    is: (val) => val === 'SOMETHING_ELSE',
    then: yup.string()
      .required(() => i18n.t('ICO.ADDRESS.OTHER_REASON_OPENING_ERROR_MESSAGE'))
      .max(60, ({ max }) => i18n.t('ICO.ADDRESS.OTHER_REASON_OPENING_ERROR_MESSAGE_TL', { count: max }))
      .matches(/^[\u4300-\u9fa5A-Za-z0-9.\-/, ]*$/, () => i18n.t(SPECIAL_ERROR))
  })
});

const Address = () => {
  const initialValues = getUserSessionDataByKey('address');
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { isPremier, lang } = getUserSessionDataByKey('global');
  const cnAddresses = getUserSessionDataByKey('cnAddresses');
  const { currentResidentialAddress: { provinceCode, cityCode }, correspondenceAddress = {} } = initialValues;
  const provinceCorrCode = correspondenceAddress.provinceCode ?? '';
  const cityCorrCode = correspondenceAddress.cityCode ?? '';
  const [provinceOptions, setProvinceOptions] = useState(getOptions(cnAddresses[CHINA_CODE]));
  const [cityOptions, setCityOptions] = useState(provinceCode ? getOptions(cnAddresses.cities?.[provinceCode]) : []);
  const [cityCorrespondenceOptions, setCityCorrespondenceOptions] = useState(provinceCorrCode ? getOptions(cnAddresses.cities?.[provinceCorrCode]) : []);
  const [districtOptions, setDistrictOptions] = useState(cityCode ? getOptions(cnAddresses?.[cityCode]) : []);
  const [districtCorrespondenceOptions, setDistrictCorrespondenceOptions] = useState(cityCorrCode ? getOptions(cnAddresses?.[cityCorrCode]) : []);
  const lan = i18n.language;
  const countryList = Country[lan];
  const ReasonForOpeningSelectOptions = ReasonForOpeningList.map(item => {
    return {
      value: item.value,
      label: item[lan]
    }
  });

  useEffect(() => {
    if (!cnAddresses[CHINA_CODE]) {
      handleCNAddresses();
    }
  }, []);

  const handleCNAddresses = async () => {
    const res = await getCNAddresses();
    const addresses = res?.data?.cnAddresses;
    setUserSessionDataByKey('cnAddresses', addresses);
    setProvinceOptions(getOptions(addresses?.[CHINA_CODE]));
  }

  useTagging({
    'page_url': TAG_PAGE_URL,
    'page_language': lang,
    'page_name': TAG_PAGE_NAME,
    'page_subcategory': 'your address',
    'raw_datalayer': '5745v9',
    'funnel_name': `account opening: ${isPremier ? 'premier' : 'one'}`,
    'funnel_step': '3',
    'funnel_step_name': 'your address'
  });

  const taggingParams = {
    'page_url': TAG_PAGE_URL,
    'page_language': lang,
    'event_category': 'error',
    'event_action': 'field validation',
    'page_name': TAG_PAGE_NAME,
    'raw_datalayer': '5745v13'
  };

  const AddressFields = (path, values, setFieldValue, i18nText) => {
    const isCorrespondencePath = path !== 'currentResidentialAddress';
    return (<div className={isCorrespondencePath ? 'correspondence-address-section' : ''}>
      {isCorrespondencePath && <Heading level={5} >{t(i18nText)}</Heading>}
      <Field name={`${path}.addressCountry`}
        onChange={(next, { value, ...rest }) => {
          if (!isCorrespondencePath && value === HKSAR) {
            setFieldValue('selectReasonsForOpeningAccount', '');
            if (value !== 'SOMETHING_ELSE') {
              setFieldValue('otherReasonForOpeningAccountInAnotherCountryText', '');
            }
          }
          if (value !== CHINA) {
            setFieldValue(`${path}.provinceCode`, '');
            setFieldValue(`${path}.cityCode`, '');
            setFieldValue(`${path}.districtCode`, '');
          } else {
            setFieldValue(`${path}.addressLine3`, '');
          }

          linkTagging({
            'page_url': TAG_PAGE_URL,
            'page_language': lang,
            'event_category': 'content',
            'event_action': 'dropdown',
            'event_subcategory': `${path}.addressCountry`,
            'event_content': `country/region: ${value}`,
            'page_name': TAG_PAGE_NAME,
            'raw_datalayer': '5745v10'
          });
          next({ value, ...rest });
        }}
      >
        <Label>{t('ICO.ADDRESS.COUNTRY')}</Label>
        <Select
          options={countryList}
          placeholder={t('ICO.COMMON.SELECT')}
        />
        <FieldFeedback />
      </Field>
      {!checkAddressCountry(path, values) && <Field name={`${path}.addressLine3`}>
        <Label>{t(`ICO.ADDRESS.LINE0`)}</Label>
        <TextInput />
        <FieldFeedback />
      </Field>}
      {checkAddressCountry(path, values) && <div>
        <Field name={`${path}.provinceCode`}
          onChange={(next, { value, ...rest }) => {
            const cnAddresses = getUserSessionDataByKey('cnAddresses');
            const options = getOptions(cnAddresses?.cities?.[value]);
            if (isCorrespondencePath) {
              setCityCorrespondenceOptions(options);
              setDistrictCorrespondenceOptions([]);
            } else {
              setCityOptions(options);
              setDistrictOptions([]);
            }
            next({ value, ...rest });
          }}>
          <Label>{t('ICO.ADDRESS.PROVINCE')}</Label>
          <Select
            options={provinceOptions}
            placeholder={t('ICO.COMMON.SELECT')}
          />
          <FieldFeedback />
        </Field>
        <Field name={`${path}.cityCode`}
          onChange={(next, { value, ...rest }) => {
            const cnAddresses = getUserSessionDataByKey('cnAddresses');
            const options = getOptions(cnAddresses?.[value]);
            if (isCorrespondencePath) {
              setDistrictCorrespondenceOptions(options);
            } else {
              setDistrictOptions(options);
            }
            next({ value, ...rest });
          }}>
          <Label>{t('ICO.ADDRESS.CITY')}</Label>
          <Select
            options={isCorrespondencePath ? cityCorrespondenceOptions : cityOptions}
            placeholder={t('ICO.COMMON.SELECT')}
          />
          <FieldFeedback />
        </Field>
        <Field name={`${path}.districtCode`}>
          <Label>{t('ICO.ADDRESS.DISTRICT')}</Label>
          <Select
            options={isCorrespondencePath ? districtCorrespondenceOptions : districtOptions}
            placeholder={t('ICO.COMMON.SELECT')}
          />
          <FieldFeedback />
        </Field>
      </div>}
      {['addressLine2', 'addressLine1'].map((name, index) => (
        <Field name={`${path}.${name}`} key={name}>
          <Label>{t(`ICO.ADDRESS.LINE${index + 1}`)}</Label>
          <TextInput />
          <FieldFeedback />
        </Field>
      ))}
      <Field name={`${path}.postalCode`}>
        <Label>{t('ICO.ADDRESS.POSTALCODE')}</Label>
        <TextInput />
        <FieldFeedback />
      </Field>
    </div>)
  };

  function cartAddressHanld({ next, value, rest }, { setFieldValue }) {
    if (value !== '2') {
      setFieldValue('atmCountry', '');
    }
    linkTagging({
      'page_url': TAG_PAGE_URL,
      'page_language': lang,
      'event_category': 'content',
      'event_action': 'radio',
      'event_content': `atm delivery: ${value !== '2' ? 'by mail' : 'collect personally'}`,
      'page_name': TAG_PAGE_NAME,
      'raw_datalayer': '5745v11'
    });
    next({ value, ...rest });
  }

  function selectReasonsForOpeningAccountHandle({ next, value, rest, setFieldValue }) {
    if (value !== 'SOMETHING_ELSE') {
      setFieldValue('otherReasonForOpeningAccountInAnotherCountryText', '');
    }
    linkTagging({
      'page_url': TAG_PAGE_URL,
      'page_language': lang,
      'event_category': 'content',
      'event_action': 'dropdown',
      'event_content': `reason for opening account: ${value}`,
      'page_name': TAG_PAGE_NAME,
      'raw_datalayer': '5745v12'
    });
    next({ value, ...rest });
  }

  return (
      <FormLayout>
        <WithI18nProgressIndicator current={2} />
        <Section>
          <Heading level={1}>{t('ICO.ADDRESS.TITLE')}</Heading>
          <Paragraph lead>{t('ICO.ADDRESS.SUB_TITLE')}</Paragraph>
          <HorizontalRule />
        </Section>
        <Form
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={({ values }) => {
            setUserSessionDataByKey('address', values);
            history.push('/info/financial');
          }}
          onChange={rePrevious}
        >
          {({ values, setFieldValue, isSubmitting, isValid, errors }) => {
            // tagging
            if (isSubmitting && !isValid && hasValidationErrors(errors)) {
              validationTagging({ errors, taggingParams });
            }
            return (
              <>
                <FormFeedback textMap={{ title: t('ICO.COMMON.CORRECT'), of: '/' }} />
                <Section>
                  <Heading level={2} accentBar>
                    {t('ICO.ADDRESS.CURRENT_ADRESS_TITLE')}
                  </Heading>
                  <Paragraph>{t('ICO.ADDRESS.CURRENT_ADRESS_SUB_TITLE')}</Paragraph>
                  <Paragraph>{t('ICO.ADDRESS.CURRENT_ADRESS_NOTE')}</Paragraph>
                  {AddressFields('currentResidentialAddress', values, setFieldValue)}
                  <Heading level={2} accentBar>{t('ICO.ADDRESS.CORRESPONDENCE_TITLE')}</Heading>
                  <Fieldset name="correspondenceAddressDifferent"
                    marginBottom={4}
                    onChange={(next, { value, ...rest }) => {
                      setFieldValue('correspondenceAddress', defaultAddressValues);
                      next({ value, ...rest });
                    }}>
                    <Label>
                      {t('ICO.ADDRESS.CORRESPONDENCE_SUB_TITLE')}
                    </Label>
                    <Hint dangerouslySetInnerHTML={{ __html: t('ICO.ADDRESS.CORRESPONDENCE_REMIND') }}></Hint>
                    <YesNoRadio textMap={{ yes: t('ICO.COMMON.YES'), no: t('ICO.COMMON.NO') }} />
                    <FieldFeedback marginTop={1} />
                  </Fieldset>
                  {values.correspondenceAddressDifferent === NO && (AddressFields('correspondenceAddress', values, setFieldValue, 'ICO.ADDRESS.CORRESPONDENCE_FORM_TITLE'))}
                  <Callout type="information" marginBottom={6}>
                    <Heading level={3}>{t('ICO.ADDRESS.EADVICESERXICE')}</Heading>
                    <UnorderedList>
                      <ListItem>{t('ICO.ADDRESS.EADVICESERXICE2')}</ListItem>
                      <ListItem>{t('ICO.ADDRESS.EADVICESERXICE3')}</ListItem>
                      <ListItem>{t('ICO.ADDRESS.EADVICESERXICE4')}</ListItem>
                    </UnorderedList>
                    <Paragraph>{t('ICO.ADDRESS.EADVICESERXICE5')}</Paragraph>
                  </Callout>
                  <Heading level={2} accentBar>{t('ICO.ADDRESS.CARDADDRESS_TITLE')}</Heading>
                  <Fieldset name="cartAddress"
                    onChange={(next, { value, ...rest }) => cartAddressHanld({ next, value, rest }, { setFieldValue })}>
                    <Label>
                      {t('ICO.ADDRESS.CARDADDRESS_SUB_TITLE')}
                    </Label>
                    {
                      CartAddressList.map(item => <Radio key={item.value} value={item.value}>{item[lang]}</Radio>)
                    }
                    <FieldFeedback marginTop={1} />
                  </Fieldset>
                  {
                    values.cartAddress === '2' &&
                    <Reveal className='customReveal' accentBar marginBottom={0}>
                      <Field name="atmCountry">
                        <Label>{t('ICO.ADDRESS.ATMCARD')}</Label>
                        <TextInput marginBottom={2} />
                        <Hint>{t('ICO.ADDRESS.ATMCARD_REMIND')}</Hint>
                        <FieldFeedback />
                      </Field>
                    </Reveal>
                  }
                  {
                    (values.currentResidentialAddress.addressCountry !== '' && values.currentResidentialAddress.addressCountry !== HKSAR) && (
                      <>
                        <Heading level={2} accentBar>{t('ICO.ADDRESS.APPLICATION_TITLE')}</Heading>
                        <Fieldset
                          marginBottom={2}
                          name="selectReasonsForOpeningAccount"
                          onChange={(next, { value, ...rest }) => selectReasonsForOpeningAccountHandle({ next, value, rest, setFieldValue })}
                        >
                          <Label>{t('ICO.ADDRESS.APPLICATION_SUB_TITLE')}</Label>
                          <Select
                            options={ReasonForOpeningSelectOptions}
                            placeholder={t('ICO.COMMON.SELECT')}
                          />
                          <FieldFeedback marginTop={1} />
                        </Fieldset>

                        {
                          ['TRAVEL_REGULARLY_TO_HK_FOR_VACATION', 'SOMETHING_ELSE'].includes(values.selectReasonsForOpeningAccount) &&
                          <section style={{ fontSize: 'var(--fontsize2)', fontWeight: 'bold' }}>
                            <Heading level={3} marginBottom={0} style={{ fontSize: 'var(--fontsize2)' }}>
                              {t('ICO.ADDRESS.REASON_OPENING_REMINDER_TITLE')}
                            </Heading>
                            <Paragraph>
                              {t(values.selectReasonsForOpeningAccount === 'SOMETHING_ELSE' ? 'ICO.ADDRESS.REASON_OPENING_OTHER_REMINDER_MESSAGE' : 'ICO.ADDRESS.REASON_OPENING_VACATION_REMINDER_MESSAGE')}
                            </Paragraph>
                          </section>
                        }

                        {
                          values.selectReasonsForOpeningAccount === 'SOMETHING_ELSE' &&
                          <Reveal className='customReveal' accentBar marginBottom={0}>
                            <Field name="otherReasonForOpeningAccountInAnotherCountryText">
                              <Label>{t('ICO.ADDRESS.OTHER_REASON_OPENING')}</Label>
                              <TextInput marginBottom={2} />
                              <FieldFeedback />
                            </Field>
                          </Reveal>
                        }
                      </>
                    )
                  }
                </Section>
                <ButtonWithBack />
              </>
            )
          }}
        </Form>
      </FormLayout>
  );
}

export default Address;
